import sumBy from "lodash/sumBy";
import upperFirst from "lodash/upperFirst";
import pluralize from "pluralize";
import { captureException } from "src/lib/monitoring";

import { GraphType } from "@hightouch/lib/query/visual/types";
import type { MetricSelectValue } from "src/components/analytics/shared/metric-selector/types";
import {
  ChartTab,
  type EventOrMetricOption,
  type Metric,
  type MetricSelection,
  type SelectedAudience,
} from "src/pages/analytics/types";
import { getMetricSelectionFromMetricOption } from "src/pages/analytics/utils";
import { AggregationOption } from "src/pages/metrics/constants";
import {
  AnalyticsFrequency,
  type ColumnReference,
  ColumnType,
  isRawColumn,
  isRelatedColumn,
  isSyntheticColumn,
  isTransformedColumn,
  type RawColumn,
  type RelatedColumn,
  type Relationship,
  type SyntheticColumn,
  type VisualQueryFilter,
} from "src/types/visual";
import { buildLargeChartUrl } from "src/utils/build-large-chart-url";
import type { BreakdownResults } from "./hooks/use-breakdown-data";
import type {
  AggregationSelectOption,
  InsightsData,
} from "./insights-chart-card";

// If `maxDecimalPlaces` is not provided, we default to no decimal places.
export const formatCount = (count: number, maxDecimalPlaces?: number) => {
  const toolTipFormatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: maxDecimalPlaces ?? 0,
    maximumFractionDigits: maxDecimalPlaces ?? 0,
  });

  return toolTipFormatter.format(count);
};

export const buildTooltipText = ({
  count,
  percentage,
  maxDecimalPlaces,
}: {
  count: number;
  percentage?: number;
  maxDecimalPlaces?: number;
}) => {
  if (percentage) {
    return `${formatCount(count, maxDecimalPlaces)} or ${(percentage * 100).toFixed(2)}%`;
  }
  return `${formatCount(count, maxDecimalPlaces)}`;
};

export const getMaxDecimalPlaces = (data: Array<BreakdownResults>) => {
  let maxDecimalPlaces = 0;

  data.forEach(({ count }) => {
    if (!Number.isFinite(count) || Number.isInteger(count)) {
      return;
    }

    const countString = count.toString();
    const decimalIndex = countString.indexOf(".");
    maxDecimalPlaces = Math.max(
      decimalIndex === -1 ? 0 : countString.length - decimalIndex - 1,
      maxDecimalPlaces,
    );
  });

  // Don't show more than 2 decimal places
  return Math.min(maxDecimalPlaces, 2);
};

/**
 * Builds the "Other" category of results for the breakdown graph.
 * @param data - A sorted list of breakdown results.
 * @param totalResultsCount - The SUM of all the result values, if available.
 * @param limit - The limit of top results to show.
 * @returns The "Other" category of results for the breakdown graph.
 */
export const buildOtherResults = ({
  data,
  totalResultsCount,
  limit = 10,
}: {
  data: Array<BreakdownResults>;
  totalResultsCount?: number | null;
  limit?: number;
}): BreakdownResults | null => {
  // If there is no need for an "Other" category, return null
  if (data.length <= limit) return null;
  // If we cannot calculate the other category, return null
  if (!totalResultsCount || totalResultsCount === 0) return null;

  const topResults = data.slice(0, limit);
  const otherCategoryCount = totalResultsCount - sumBy(topResults, "count");

  return {
    value: "Other",
    count: otherCategoryCount,
    percentage: otherCategoryCount / totalResultsCount,
  };
};

export const buildAggregationMethodOptions = ({
  parentModelId,
  metric,
  groupBy,
}: {
  parentModelId: string;
  metric: MetricSelectValue;
  groupBy: MetricSelectValue | null;
}): AggregationSelectOption[] => {
  const savedMetric = Boolean(metric.metricId);
  const relatedModel = Boolean(metric.relationshipId);
  const isParentModel =
    metric.modelId?.toString() === parentModelId?.toString();
  const isNumberColumn = metric.columnType === ColumnType.Number;

  const parentModelRowNoun = "user";

  // If the metric is a saved metric, we don't let the user customize it
  if (savedMetric)
    return [
      {
        label: "Total",
        // For saved metrics, we ignore the aggregation method.
        value: AggregationOption.Count,
      },
    ];

  // Related models (e.g. events) can specify aggregation methods per user
  if (relatedModel && groupBy && isNumberColumn) {
    return [
      {
        label: `Average of ${upperFirst(metric.columnName)}`,
        value: AggregationOption.AverageOfProperty,
      },
      {
        label: `Sum of ${upperFirst(metric.columnName)}`,
        value: AggregationOption.SumOfProperty,
      },
      {
        label: `Average of ${upperFirst(metric.columnName)} per ${parentModelRowNoun}`,
        value: AggregationOption.AverageOfPropertyPerUser,
      },
    ];
  }

  // Parent models implicitly average per user, so no need to include it
  if (groupBy && isNumberColumn) {
    return [
      {
        label: `Average of ${upperFirst(metric.columnName)}`,
        value: AggregationOption.AverageOfProperty,
      },
      {
        label: `Sum of ${upperFirst(metric.columnName)}`,
        value: AggregationOption.SumOfProperty,
      },
    ];
  }

  if (isParentModel) {
    return [
      {
        label: `Total ${pluralize(parentModelRowNoun)}`,
        value: AggregationOption.Count,
      },
    ];
  }

  const modelRowNoun = metric.eventModelId ? "events" : "rows";
  return [
    {
      label: `Total ${modelRowNoun}`,
      value: AggregationOption.Count,
    },
    {
      label: `Unique ${pluralize(parentModelRowNoun)}`,
      value: AggregationOption.UniqueUsers,
    },
  ];
};

/**
 * Converts the form data to the appropriate properties to build
 * our analytics API request.
 *
 * This functionality is specific to our small audience insight charts.
 * If there is a metric selected, and no group by, we implicitly
 * group by / break down the audience metric. In this case, the metric selection
 * should include the model ID / event model ID, but *not* the column / trait we are targeting.
 *
 * If there is a group by, we are explicitly grouping by the group by column,
 * and the metric selection should include the column / trait we are targeting.
 *
 * Saved Metrics are the exception to this rule. If a saved metric is selected,
 * we do not implicitly group by the metric column.
 *
 * @param parentModelId - The ID of the parent model.
 * @param data - The form data, including the `metric` and `groupBy` properties.
 * @param events - The events, used to build the metric selection.
 * @param metrics - The metrics, used to build the metric selection.
 * @returns The metric selection and group by column for our analytics API request.
 */
export const convertFormDataToMetricPropsForChart = ({
  parentModelId,
  data,
  events,
  metrics,
}: {
  parentModelId: string;
  data: InsightsData;
  events: Relationship[];
  metrics: Metric[];
}):
  | {
      metricSelection: MetricSelection;
      groupBy: ColumnReference;
    }
  | undefined => {
  const { metric, groupBy, aggregationMethod } = data;

  if (!metric) return undefined;

  const id = metric.metricId || metric.relationshipId || parentModelId;

  // If there is an explicit groupBy, we want to measure the trait / column.
  const traitId = groupBy ? metric.traitId : undefined;
  const name = groupBy ? metric.columnName : metric.modelName;

  const metricOption: EventOrMetricOption = {
    id,
    eventModelId: metric.eventModelId,
    modelId: metric.modelId,
    traitId,
    name: name || "Metric",
    description: null,
    relationshipId: metric.relationshipId,
  };

  // Built the metric selection from our metric option
  const metricSelection = getMetricSelectionFromMetricOption({
    metricOption,
    events,
    metrics,
  });

  if (!metricSelection) return undefined;

  // We only want to measure by the metric column if there is no groupBy
  const metricColumn = groupBy?.column ? metric.column : undefined;

  // If there is an explicit groupBy, use it. Otherwise, we implicitly group by the metric
  const groupByColumn = groupBy?.column
    ? groupBy.column.column_reference
    : metric.column?.column_reference;

  return {
    metricSelection: {
      ...metricSelection,
      aggregationMethod: aggregationMethod ?? AggregationOption.Count,
      column: metricColumn,
    },
    groupBy: groupByColumn,
  };
};

export const buildSelectedAudiences = ({
  parentModelId,
  name,
  filter,
  subsetIds,
  audienceId,
  isStaticList,
}: {
  parentModelId: string;
  name: string;
  filter: VisualQueryFilter | null;
  subsetIds?: string[];
  audienceId?: string;
  isStaticList?: boolean;
}): SelectedAudience[] | null => {
  if (!filter) return null;

  // Static-list audiences (e.g. CSV imports) store membership in a materialized
  // table rather than in the saved filter conditions, so an inline cohort built
  // from the parent model + filter would count the entire parent model. Route
  // them through the saved audience id so the backend restricts to members.
  if (isStaticList && audienceId) {
    return [
      {
        id: Number(audienceId),
        name,
        splits: [],
        subsetIds,
      },
    ];
  }

  // NOTE: We don't support split definitions or
  // additional columns (e.g. audience traits) in breakdowns at the moment
  const {
    additionalColumns: _additionalColumns,
    splitTestDefinition: _splitTestDefinition,
    ...visualQueryFilter
  } = filter;

  return [
    {
      id: Number(parentModelId),
      name,
      filter: visualQueryFilter,
      splits: [],
      subsetIds,
    },
  ];
};

export const isValidGroupByColumn = (
  column: ColumnReference | null,
): column is RawColumn | RelatedColumn | SyntheticColumn => {
  if (!column) return false;
  if (
    isRawColumn(column) ||
    isRelatedColumn(column) ||
    isTransformedColumn(column) ||
    isSyntheticColumn(column)
  ) {
    return true;
  }

  return false;
};

export const buildInsightsChartUrl = ({
  parentModelId,
  metricSelection,
  groupByColumn,
  audienceName = "Audience X",
  filter,
  subsetIds,
  audienceId,
  isStaticList,
}: {
  parentModelId: string;
  metricSelection: MetricSelection;
  groupByColumn?: ColumnReference;
  audienceName?: string;
  filter?: VisualQueryFilter;
  subsetIds?: string[];
  audienceId?: string;
  isStaticList?: boolean;
}): string | null => {
  if (groupByColumn && !isValidGroupByColumn(groupByColumn)) {
    captureException(new Error("Invalid groupByColumn"), {
      extra: {
        groupByColumn,
      },
    });

    return null;
  }

  const selectedAudiences = buildSelectedAudiences({
    parentModelId: parentModelId.toString(),
    name: audienceName,
    filter: filter ?? null,
    subsetIds,
    audienceId,
    isStaticList,
  });

  if (!selectedAudiences) {
    captureException(new Error("Invalid selectedAudiences"), {
      extra: {
        selectedAudiences,
      },
    });

    return null;
  }

  const url = buildLargeChartUrl({
    parentModelId: Number(parentModelId),
    metricSelection: [metricSelection],
    groupByColumns: groupByColumn ? [groupByColumn] : [],
    selectedAudiences,
    chartTab: ChartTab.Insights,
    graphType: GraphType.Bar,
    rollupFrequency: AnalyticsFrequency.All,
  });

  return url;
};
